import { ref, computed, watch, shallowRef } from 'vue';
import { useRoute } from 'vue-router';
import { defineStore } from 'pinia';
import { useNotifications } from './notifications.store';
import { setAuthState } from '../composables/auth';

import resumesApi, { retrieveResumes, deleteResumes } from '../api/builder';

export const useResumesStore = defineStore('resumes', () => {
	const route = useRoute();

	const { isAuthenticated } = setAuthState();
    
	const resumes = ref([]);
	const formattedResumes = ref([]);
	const unformattedResumes = ref([]);
    
	const loading = shallowRef(false);
	const loaded = shallowRef(false);
	const duplicateLoading = shallowRef(false);
	const hasResumesLoaded = shallowRef(false);

	const notificationsStore = useNotifications();

	const fetchResumes = async () => {
		if (!isAuthenticated()) {return;}

		loading.value = true;
		if (hasResumesLoaded.value === true) {hasResumesLoaded.value = false;}

		try {
			const res = await retrieveResumes();
			resumes.value = res;
		} catch {
			notificationsStore.notify('Failed to retrieve resumes!', 'error');
		} finally {
			hasResumesLoaded.value = true;
			loading.value = false;
			loaded.value = true;
		}
	};
    
	const duplicateResume = async (resumeId) => {
		duplicateLoading.value = true;
      
		try {
			const newResume = await resumesApi.resume.duplicate(resumeId);
			resumes.value.unshift(newResume);
        
			notificationsStore.notify('Resume was duplicated successfully!', 'success');
		} catch {
			notificationsStore.notify('Failed to duplicate resume!', 'error');
		} finally {
			duplicateLoading.value = false;
		}
	};

	const getResumeById = (resumeId) => {
		return resumes.value.find((resume) => {
			return resume.uuid === resumeId;
		});
	};
    
	const deleteResume = async (resumeId) => {
		await deleteResumes(resumeId);
        
		const deletedResumeIndex = resumes.value.findIndex((r) => r.uuid === resumeId);
		resumes.value.splice(deletedResumeIndex, 1);
	};

	const addResumeToStore = (resume) => {
		resumes.value.push(resume);
	};
    
	const autocompleteResume = computed(() => 
		resumes.value.map((resume) => ({
			id: resume?.uuid,
			label: resume?.title?.replace(/<[^>]*>/g, '') ?? 'Untitled',
		}))
	);
    
	watch(resumes, (newValue) => {
		const formatted = [], unformatted = [];
        
		newValue.forEach((resume) => {
			if (!!resume?.keep_original_format === false) {
				formatted.push(resume);
			} else {
				unformatted.push(resume);
			}
		});
        
		formattedResumes.value = formatted;
		unformattedResumes.value = unformatted;
	}, {
		deep: true, // TODO: refactor resumes store to avoid deep watching
	});

	const getLastResumeId = () => {
		return route.name === 'resumes.edit' ? route.params.id : (
			resumes.value?.length ? resumes.value[0]?.uuid : null
		);
	};
  
	return {
		fetchResumes,
		addResumeToStore,
		getResumeById,
		duplicateResume,
		getLastResumeId,
		hasResumesLoaded,
		duplicateLoading,

		resumes,
		formattedResumes,
		unformattedResumes,
		autocompleteResume,
		deleteResume,

		loading,
		loaded
	};
});
